import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { ThemeContext } from '../ThemeContext';
import api from '../api';
import ImageViewer from '../components/ImageViewer';
import { motion } from 'framer-motion';
import { Clock, Calculator as CalculatorIcon, X } from 'lucide-react';
import Calculator from '../components/Calculator';

const MODULE_CONFIG = {
  rw1: {
    title: 'Reading & Writing Module 1',
    questionCount: 27,
    time: 32 * 60, // seconds
    contentName: 'english',
    module: 1,
  },
  rw2: {
    title: 'Reading & Writing Module 2',
    questionCount: 27,
    time: 32 * 60, // seconds
    contentName: 'english',
    module: 2,
  },
  math1: {
    title: 'Math Module 1',
    questionCount: 22,
    time: 35 * 60, // seconds
    contentName: 'math',
    module: 1,
  },
  math2: {
    title: 'Math Module 2',
    questionCount: 22,
    time: 35 * 60, // seconds
    contentName: 'math',
    module: 2,
  },
};

const PHASES = ['rw1', 'rw2', 'break', 'math1', 'math2', 'end'];
const BREAK_TIME = 10 * 60; // 10 minutes in seconds

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  // Hover colors: Light mode: #06bae4, Dark mode: #ff9e42
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.3)}, 0 0 20px ${hexToRgba(hoverColor, 0.5)}, 0 0 40px ${hexToRgba(hoverColor, 0.3)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      {children}
    </motion.div>
  );
};

const SatTestPage = () => {
  const [phase, setPhase] = useState('rw1');
  const [showModuleModal, setShowModuleModal] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showBreakTimerModal, setShowBreakTimerModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionStates, setQuestionStates] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [textAnswers, setTextAnswers] = useState({}); // For Grid-in questions
  const [timer, setTimer] = useState(0);
  const [navigationBlocked, setNavigationBlocked] = useState(false);
  const [fromPractice, setFromPractice] = useState(false);
  const [showQuestionNav, setShowQuestionNav] = useState(true);
  const timerRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const { isDarkMode } = useContext(ThemeContext);
  const [startCountdown, setStartCountdown] = useState(5);
  const startCountdownRef = useRef(null);
  const [submitCountdown, setSubmitCountdown] = useState(10);
  const submitCountdownRef = useRef();
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [submitModalFromTimeout, setSubmitModalFromTimeout] = useState(false);
  const questionNavRef = useRef(null);
  const [questionNavHeight, setQuestionNavHeight] = useState('0px');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalTimer, setErrorModalTimer] = useState(10);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [eliminatedOptions, setEliminatedOptions] = useState({}); // Keyed by question_id

  // Compute currentQuestion early so it can be used in useEffect hooks
  const currentQuestion = questions[currentQuestionIndex];

  // --- Navigation/Reload Protection ---
  useEffect(() => {
    // Only allow access if coming from Practice page
    if (location.state && location.state.fromPractice) {
      setFromPractice(true);
      sessionStorage.setItem('satTestFromPractice', 'true');
    } else if (sessionStorage.getItem('satTestFromPractice') === 'true') {
      setFromPractice(true);
    } else {
      navigate('/practice');
    }
  }, [location, navigate]);

  useEffect(() => {
  if (questionNavRef.current) {
    if (showQuestionNav) {
      setQuestionNavHeight(`${questionNavRef.current.scrollHeight}px`);
    } else {
      setQuestionNavHeight('0px');
    }
  }
}, [showQuestionNav, questions.length]); // recalculate on toggle or when questions change


  useEffect(() => {
    // Block reload/close
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // --- Timer Logic ---
  useEffect(() => {
    if (!sessionStarted || timer <= 0) return;
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSectionTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [sessionStarted, timer]);
  const renderTextWithPi = (text) => {
    if (!text.includes('π')) return text;
    
    return text.split('π').map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < text.split('π').length - 1 && (
          <span className="font-sans">π</span>
        )}
      </React.Fragment>
    ));
  };
  // Add countdown effect for start button
  useEffect(() => {
    if (startCountdown > 0 && showModuleModal) {
      startCountdownRef.current = setInterval(() => {
        setStartCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(startCountdownRef.current);
            handleStartModule();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (startCountdownRef.current) {
        clearInterval(startCountdownRef.current);
      }
    };
  }, [showModuleModal]);

  // Reset countdown when modal is shown
  useEffect(() => {
    if (showModuleModal) {
      setStartCountdown(5);
    }
  }, [showModuleModal]);

  // Error modal countdown timer
  useEffect(() => {
    if (showErrorModal && errorModalTimer > 0) {
      const interval = setInterval(() => {
        setErrorModalTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            handleErrorModalTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showErrorModal, errorModalTimer]);

  // Close calculator if question changes to non-math
  useEffect(() => {
    if (currentQuestion) {
      const categoryType = currentQuestion.category_type?.toLowerCase() || '';
      const contentName = currentQuestion.content_name?.toLowerCase() || '';
      const isMathCategory = categoryType === 'math' || 
                            categoryType === 'all math' ||
                            categoryType.includes('math') ||
                            contentName === 'math' ||
                            MODULE_CONFIG[phase]?.contentName === 'math';
      if (!isMathCategory && isCalculatorOpen) {
        setIsCalculatorOpen(false);
      }
    }
  }, [currentQuestionIndex, currentQuestion, isCalculatorOpen, phase]);

  // --- Start Module ---
  const handleStartModule = async () => {
    setShowModuleModal(false);
    setIsLoading(true);
    setSessionStarted(false);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setQuestionStates({});
    setSelectedAnswers({});
    setTextAnswers({}); // Reset text answers
    if (phase === 'break') {
      console.log('break');
      setShowBreakTimerModal(true);
      setTimer(BREAK_TIME);
      setIsLoading(false);
      return;
    }
    // Start or resume session for first module
    if (!sessionId) {
      try {
        const sessionResponse = await api.post('/api/solo/start_or_resume_session/', {
          content_name: 'practice',
        });
        setSessionId(sessionResponse.data.session_id);
      } catch (error) {
        setIsLoading(false);
        alert('Failed to start session.');
        return;
      }
    }
    // Fetch questions
    try {
      const response = await api.get('/api/practice/get_questions_practice/', {
        params: {
          content_name: MODULE_CONFIG[phase].contentName,
          difficulty: 'easy',
        },
      });
      if (response.data.questions) {
        setQuestions(response.data.questions);
        // Initialize question states and selected answers
        const initialStates = {};
        const initialSelected = {};
        const initialTextAnswers = {};
        response.data.questions.forEach((q) => {
          initialStates[q.question_id] = 'unvisited';
          initialSelected[q.question_id] = null;
          initialTextAnswers[q.question_id] = ''; // Initialize empty text answers
        });
        setQuestionStates(initialStates);
        setSelectedAnswers(initialSelected);
        setTextAnswers(initialTextAnswers);
        setSessionStarted(true);
        setTimer(MODULE_CONFIG[phase].time);
        setStartCountdown(5);
      }
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      setShowErrorModal(true);
      setErrorModalTimer(5);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  // --- Section Timeout or Submit ---
  const handleSectionTimeout = () => {
    setSubmitModalFromTimeout(true);
    setShowSubmitModal(true);
  };

  const handleSubmitSection = () => {
    // Save the final answer for Grid-in questions before showing the modal
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion && currentQuestion.question_type === 'Grid-in') {
      saveCurrentAnswer();
    }
    setSubmitModalFromTimeout(false);
    setShowSubmitModal(true);
  };

  const handleConfirmSubmit = () => {
    setShowSubmitModal(false);
    goToNextPhase();
  };

  const handleCancelSubmit = () => {
    setShowSubmitModal(false);
    setSessionStarted(true);
    setTimer((t) => t > 0 ? t : MODULE_CONFIG[phase]?.time || 0);
  };

  const handleErrorModalTimeout = () => {
    setShowErrorModal(false);
    navigate('/practice');
  };

  const handleErrorModalButtonClick = () => {
    setShowErrorModal(false);
    navigate('/practice');
  };

  // --- Go to Next Phase ---
  const goToNextPhase = () => {
    setSessionStarted(false);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setQuestionStates({});
    setSelectedAnswers({});
    setTextAnswers({}); // Reset text answers
    if (phase === 'rw1') {
      setPhase('rw2');
      setShowModuleModal(true);
      setShowBreakTimerModal(false);
    } else if (phase === 'rw2') {
      setPhase('break');
      setShowModuleModal(false);
      setShowBreakTimerModal(true);
      setTimer(BREAK_TIME);
    } else if (phase === 'break') {
      setPhase('math1');
      setShowModuleModal(true);
      setShowBreakTimerModal(false);
    } else if (phase === 'math1') {
      setPhase('math2');
      setShowModuleModal(true);
      setShowBreakTimerModal(false);
    } else if (phase === 'math2') {
      setPhase('end');
      setShowModuleModal(true);
      setShowBreakTimerModal(false);
    }
  };

  // --- Break Logic ---
  const handleBreakEnd = () => {
    setShowBreakTimerModal(false);
    setPhase('math1');
    setShowModuleModal(true);
  };

  // Add useEffect to handle break timer
  useEffect(() => {
    if (showBreakTimerModal && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            handleBreakEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showBreakTimerModal, timer]);

  // --- End Session ---
  useEffect(() => {
    if (phase === 'end') {
      setReportLoading(true);
      setReportError(null);
      setReportSuccess(false);
      // End session first
      api.post('/api/solo/end_session/', {
        content_name: 'practice',
        session_id: sessionId,
      })
        .then(() => {
          sessionStorage.removeItem('satTestFromPractice');
          // Now generate the report
          return api.post('/api/practice/generate_report/', {
            session_id: sessionId,
          });
        })
        .then((res) => {
          if (res.data && res.data.test_id) {
            setReportId(res.data.test_id);
            setReportSuccess(true);
          } else {
            setReportError('Failed to generate report. Please try again.');
          }
        })
        .catch((error) => {
          setReportError('There was an error generating the report. Please try again.');
        })
        .finally(() => {
          setSessionStarted(false);
          setQuestions([]);
          setCurrentQuestionIndex(0);
          setQuestionStates({});
          setSelectedAnswers({});
          setTextAnswers({}); // Reset text answers
          setTimer(0);
          setShowModuleModal(false);
          setShowBreakTimerModal(false);
          setReportLoading(false);
        });
    }
    // eslint-disable-next-line
  }, [phase]);

  // --- Question/Answer/Flag Logic ---
  const handleQuestionClick = (index) => {
    // Save current answer before navigating
    saveCurrentAnswer();
    setCurrentQuestionIndex(index);
  };

  const handleFlagQuestion = (questionId) => {
    setQuestionStates((prev) => {
      if (prev[questionId] === 'flagged') {
        if (selectedAnswers[questionId] || textAnswers[questionId]) {
          return { ...prev, [questionId]: 'answered' };
        } else {
          return { ...prev, [questionId]: 'visited' };
        }
      } else {
        return { ...prev, [questionId]: 'flagged' };
      }
    });
  };

  const handleAnswerSelect = async (option) => {
    const currentQuestion = questions[currentQuestionIndex];
    // Don't allow selecting eliminated options
    const questionEliminated = eliminatedOptions[currentQuestion.question_id] || new Set();
    if (questionEliminated.has(option)) return;
    
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestion.question_id]: option,
    }));
    setQuestionStates((prev) => ({
      ...prev,
      [currentQuestion.question_id]: 'answered',
    }));
    try {
      await api.post('/api/user/update_question_score/', {
        question_id: currentQuestion.question_id,
        option_selected: option,
        session_id: sessionId,
        practice: true,
        module: MODULE_CONFIG[phase].module,
      });
    } catch (error) {
      // Optionally show error
    }
  };

  const handleToggleEliminate = (option, e) => {
    e.stopPropagation();
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;
    
    const questionId = currentQuestion.question_id;
    setEliminatedOptions((prev) => {
      const questionEliminated = prev[questionId] || new Set();
      const newSet = new Set(questionEliminated);
      
      if (newSet.has(option)) {
        newSet.delete(option);
        // If this option was selected, clear selection
        if (selectedAnswers[questionId] === option) {
          setSelectedAnswers((prev) => ({
            ...prev,
            [questionId]: null,
          }));
        }
      } else {
        newSet.add(option);
        // If this option was selected, clear selection
        if (selectedAnswers[questionId] === option) {
          setSelectedAnswers((prev) => ({
            ...prev,
            [questionId]: null,
          }));
        }
      }
      
      return {
        ...prev,
        [questionId]: newSet,
      };
    });
  };

  // Handle text input for Grid-in questions
  const handleTextAnswerChange = (value) => {
    const currentQuestion = questions[currentQuestionIndex];
    setTextAnswers((prev) => ({
      ...prev,
      [currentQuestion.question_id]: value,
    }));
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestion.question_id]: value, // Use text value as selected answer
    }));
    setQuestionStates((prev) => ({
      ...prev,
      [currentQuestion.question_id]: 'answered',
    }));
  };

  // Save current answer when navigating away
  const saveCurrentAnswer = async () => {
    if (!sessionStarted || questions.length === 0) return;
    
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    const currentAnswer = selectedAnswers[currentQuestion.question_id];
    if (currentAnswer) {
      try {
        await api.post('/api/user/update_question_score/', {
          question_id: currentQuestion.question_id,
          option_selected: currentAnswer,
          session_id: sessionId,
          practice: true,
          module: MODULE_CONFIG[phase].module,
        });
      } catch (error) {
        console.error('Error saving answer:', error);
      }
    }
  };
  //Auto-navigate to report page when ready
  useEffect(() => {
    if (reportSuccess && reportId) {
      navigate('/sat-test-report', { state: { reportId, fromSatTest: true } });
    }
  }, [reportSuccess, reportId, navigate]);
  // Save question as visited when navigating to it
  useEffect(() => {
    if (sessionStarted && questions.length > 0) {
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion && questionStates[currentQuestion.question_id] === 'unvisited') {
        setQuestionStates((prev) => ({
          ...prev,
          [currentQuestion.question_id]: 'visited',
        }));
        api.post('/api/user/add_content_score/', {
          content_name: currentQuestion.content_name,
          question_id: currentQuestion.question_id,
          category_type: currentQuestion.category_type,
          question_status: 'unanswered',
          option_selected: 'Not answered',
          session_id: sessionId,
          practice: true,
          module: MODULE_CONFIG[phase].module,
        }).catch((error) => {});
      }
    }
    // eslint-disable-next-line
  }, [currentQuestionIndex, questions, sessionStarted, sessionId, user, questionStates, phase]);

  // Reset eliminated options when question changes
  useEffect(() => {
    if (currentQuestion) {
      const questionId = currentQuestion.question_id;
      // Initialize empty set for this question if it doesn't exist
      setEliminatedOptions((prev) => {
        if (!prev[questionId]) {
          return {
            ...prev,
            [questionId]: new Set(),
          };
        }
        return prev;
      });
    }
  }, [currentQuestionIndex, currentQuestion]);

  // --- Legend Counts ---
  const legendCounts = { unvisited: 0, visited: 0, answered: 0, flagged: 0 };
  Object.values(questionStates).forEach((state) => {
    if (legendCounts[state] !== undefined) legendCounts[state]++;
  });

  // --- Unanswered Count ---
  const unansweredCount = Object.values(questionStates).filter(
    (state) => state !== 'answered' && state !== 'flagged'
  ).length;

  // --- Timer Display ---
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // --- Navigation Buttons ---
  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      saveCurrentAnswer(); // Save current answer before navigating
      setShowSubmitModal(false);
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      saveCurrentAnswer(); // Save current answer before navigating
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  // --- In-app Navigation Protection (optional, can be expanded) ---
  // ...

  // --- UI ---
  const selectedAnswer = currentQuestion ? selectedAnswers[currentQuestion.question_id] : null;
  const textAnswer = currentQuestion ? textAnswers[currentQuestion.question_id] : '';
  const moduleConfig = MODULE_CONFIG[phase];

  // Add debug log for phase and modal states
  console.log('phase:', phase, 'showModuleModal:', showModuleModal, 'showBreakTimerModal:', showBreakTimerModal);

  useEffect(() => {
    if (showSubmitModal) {
      setSubmitCountdown(10);
      submitCountdownRef.current = setInterval(() => {
        setSubmitCountdown(prev => {
          if (prev <= 1) {
            clearInterval(submitCountdownRef.current);
            handleConfirmSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      clearInterval(submitCountdownRef.current);
    };
    // eslint-disable-next-line
  }, [showSubmitModal]);

  if (isLoading || reportLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 font-zonaProRegular pb-24">
        <Card delay={0} className="text-center max-w-md w-full">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-6"
          >
            <svg className="h-12 w-12" style={{ color: '#34c759' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </motion.div>
          <p className="text-xl text-gray-600 dark:text-gray-100 font-semibold">
            {reportLoading ? 'Generating test report...' : 'Loading your test...'}
          </p>
        </Card>
      </div>
    );
  }

  if (reportError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 font-zonaProRegular pb-24">
        <Card delay={0} className="text-center max-w-md w-full">
          <p className="text-xl text-red-600 dark:text-red-400 font-semibold mb-6">{reportError}</p>
          <motion.button
            onClick={() => navigate('/practice')}
            className="px-8 py-3 bg-blue-600 text-white rounded-full shadow hover:bg-blue-700 text-lg font-semibold"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Go Back to Practice
          </motion.button>
        </Card>
      </div>
    );
  }

  if (reportSuccess && reportId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 font-zonaProRegular pb-24">
        <Card delay={0} className="text-center max-w-md w-full">
        <p className="text-xl text-green-600 font-semibold mb-6">Taking you to your report...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">
        {/* Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold font-zonaProUltra mb-3 sm:mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            {phase === 'break' ? 'Break Time' : moduleConfig?.title || 'SAT Practice Test'}
          </h1>
          {sessionStarted && moduleConfig && (
            <motion.p
              className="text-gray-600 dark:text-gray-200 text-sm sm:text-base md:text-lg font-zonaProRegular"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {moduleConfig.questionCount} questions • {moduleConfig.time / 60} minutes
            </motion.p>
          )}
        </motion.div>

        {/* Timer and Question Navigation Bar */}
        {sessionStarted && questions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="w-full max-w-7xl mx-auto mb-8"
          >
            <div className="flex flex-col md:flex-row gap-4 items-stretch">

              {/* Question Navigation Card - Takes remaining space */}
              <Card delay={0.2} className="flex-1 min-w-0">
                <div className="flex flex-col w-full">
                  {/* Header with Status and Toggle */}
                  <div className="w-full flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-3">
                    <span className="text-gray-500 dark:text-gray-300 text-xs sm:text-sm font-semibold">Questions Status</span>
                    <div className="flex flex-wrap gap-2 sm:gap-4 justify-center md:justify-end items-center">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <span className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 inline-block"></span>
                        <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-200">Unvisited: {legendCounts.unvisited}</span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <span className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600 inline-block"></span>
                        <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-200">Unanswered: {legendCounts.visited}</span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <span className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-green-500 dark:bg-green-600 border border-green-700 dark:border-green-800 inline-block"></span>
                        <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-200">Answered: {legendCounts.answered}</span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <span className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-orange-500 dark:bg-orange-600 border border-orange-700 dark:border-orange-800 inline-block"></span>
                        <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-200">Flagged: {legendCounts.flagged}</span>
                      </div>
                      <motion.button
                        onClick={() => setShowQuestionNav((prev) => !prev)}
                        className="text-xs px-2 sm:px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 font-semibold transition-colors flex-shrink-0"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {showQuestionNav ? 'Hide' : 'Show'}
                      </motion.button>
                    </div>
                  </div>

                  {/* Question Navigation Buttons */}
                  <div
                    style={{
                      maxHeight: questionNavHeight,
                      transition: 'max-height 0.8s ease-in-out, opacity 0.8s ease-in-out',
                      opacity: showQuestionNav ? 1 : 0,
                      overflow: 'hidden',
                    }}
                    className="flex flex-wrap gap-2 justify-center w-full"
                    ref={questionNavRef}
                  >
                    {questions.map((question, index) => {
                      const state = questionStates[question.question_id];
                      let bgColor = 'bg-gray-100 dark:bg-gray-700';
                      if (state === 'answered') bgColor = 'bg-green-500 dark:bg-green-600';
                      else if (state === 'flagged') bgColor = 'bg-orange-500 dark:bg-orange-600';
                      else if (state === 'visited') bgColor = 'bg-blue-100 dark:bg-blue-900/30';
                      return (
                        <button
                          key={index}
                          onClick={() => handleQuestionClick(index)}
                          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-all
            ${currentQuestionIndex === index ? 'ring-2 ring-blue-600 dark:ring-blue-400' : ''} ${bgColor} text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600`}
                        >
                          {index + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Card>
              {/* Timer Card - Small */}
              <Card delay={0.1} className="flex-shrink-0 flex items-center justify-center">
                <div className="flex flex-col items-center justify-center text-center min-w-[100px] sm:min-w-[140px] py-3 sm:py-4">
                  <Clock className={`w-5 h-5 sm:w-6 sm:h-6 mb-1 sm:mb-2 ${timer <= 60 && timer > 0 ? 'text-red-500 dark:text-red-400' : isDarkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Timer</span>
                  <p className={`text-xl sm:text-2xl font-bold font-zonaProUltra ${timer <= 60 && timer > 0 ? 'text-red-500 dark:text-red-400 animate-pulse' : 'text-gray-700 dark:text-white'}`}>
                    {formatTime(timer)}
                  </p>
                </div>
              </Card>
            </div>
          </motion.div>
        )}

        {/* Question Display */}
        {sessionStarted && questions.length > 0 && (
          <div className="w-full max-w-7xl mx-auto">
            <Card delay={0.3} className="w-full">
              {currentQuestion && !isLoading ? (
                <div className="md:flex md:gap-8">
                  <div className="md:w-3/5 lg:w-1/2 space-y-5">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                      <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 dark:text-white font-zonaProUltra">Question:</h1>
                      {(() => {
                        const categoryType = currentQuestion.category_type?.toLowerCase() || '';
                        const contentName = currentQuestion.content_name?.toLowerCase() || '';
                        const isMathCategory = categoryType === 'math' || 
                                              categoryType === 'all math' ||
                                              categoryType.includes('math') ||
                                              contentName === 'math' ||
                                              MODULE_CONFIG[phase]?.contentName === 'math';
                        return isMathCategory ? (
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsCalculatorOpen(!isCalculatorOpen);
                            }}
                            className={`p-2 rounded-lg transition-all duration-200 ${
                              isCalculatorOpen
                                ? 'bg-[#ff9e42] text-white'
                                : isDarkMode
                                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                            }`}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Open Calculator"
                          >
                            <CalculatorIcon className="w-5 h-5" />
                          </motion.button>
                        ) : null;
                      })()}
                      <motion.button
                        onClick={() => handleFlagQuestion(currentQuestion.question_id)}
                        className={`px-3 py-1 rounded-full text-sm transition-all duration-200 min-w-[110px] text-center ${
                          questionStates[currentQuestion.question_id] === 'flagged'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        style={{ width: '110px' }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {questionStates[currentQuestion.question_id] === 'flagged' ? 'Flagged' : 'Flag for Review'}
                      </motion.button>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-200 mb-2 sm:mb-3">Read the question carefully and select the best answer.</p>
                    {currentQuestion.image_name && (
                      <div className="my-4 rounded-lg overflow-hidden shadow-lg max-h-100 flex justify-center items-center bg-gray-50 dark:bg-gray-800">
                        <ImageViewer fileName={currentQuestion.image_name} alt="Question image" className="max-h-full max-w-full object-contain" />
                      </div>
                    )}
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-700 dark:text-gray-100 text-left leading-relaxed whitespace-pre-line">
                      {renderTextWithPi(currentQuestion.question_choice)}
                    </p>
                  </div>
                  {/* Options on the right */}
                  <div className="md:w-2/5 lg:w-1/2 md:pl-4 lg:pl-6">
                    <h2 className="text-base sm:text-lg md:text-xl font-zonaProUltra text-gray-700 dark:text-gray-100 mb-3 sm:mb-4">
                      {currentQuestion.question_type === "Grid-in" ? "Enter your answer:" : "Choose an Answer:"}
                    </h2>
                    {currentQuestion.question_type === "Grid-in" ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={textAnswer}
                          onChange={(e) => handleTextAnswerChange(e.target.value)}
                          className="w-full p-2 sm:p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff9e42] text-sm sm:text-base md:text-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600"
                          placeholder="Type your answer here"
                        />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {['a', 'b', 'c', 'd'].map((option) => {
                          const optionValue = renderTextWithPi(currentQuestion[`option_${option}`]);
                          if (!optionValue) return null;
                          const questionId = currentQuestion.question_id;
                          const questionEliminated = eliminatedOptions[questionId] || new Set();
                          const isEliminated = questionEliminated.has(option);
                          const baseClasses = "flex-1 text-left p-3 sm:p-4 rounded-lg border-2 transform hover:scale-[1.02] focus:outline-none text-sm sm:text-base md:text-lg transition-transform duration-150 ease-in-out";
                          const selectedClasses = "!bg-[#ff9e42] !border-[#ff9e42] text-white font-bold shadow-md ring-2 ring-[#ff9e42]/30";
                          const unselectedClasses = "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-[#ff9e42] focus:border-[#ff9e42] focus:ring-1 focus:ring-[#ff9e42]/20 active:bg-[#ff9e42] active:border-[#ff9e42] active:text-white";
                          const eliminatedClasses = isEliminated ? "opacity-50 cursor-not-allowed line-through" : "";
                          const finalClasses = `${baseClasses} ${selectedAnswer === option ? selectedClasses : unselectedClasses} ${eliminatedClasses}`;
                          return (
                            <div key={option} className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleAnswerSelect(option)}
                                className={finalClasses}
                                disabled={isEliminated}
                                style={selectedAnswer === option ? { backgroundColor: '#ff9e42', borderColor: '#ff9e42', color: 'white' } : {}}
                              >
                                <span className={`${isEliminated ? 'line-through' : ''}`}>
                                  <span className="font-bold mr-2">{option.toUpperCase()}</span>{optionValue}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={(e) => handleToggleEliminate(option, e)}
                                className={`p-1.5 sm:p-2 rounded-full transition-all duration-200 flex-shrink-0 ${
                                  isEliminated
                                    ? 'bg-red-500 hover:bg-red-600 text-white'
                                    : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300'
                                }`}
                                title={isEliminated ? "Restore option" : "Eliminate option"}
                              >
                                <X className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[300px]">
                  {isLoading && (
                    <p className="text-xl text-gray-500 dark:text-gray-200 font-semibold font-zonaProRegular">Loading Question...</p>
                  )}
                </div>
              )}
              {/* Navigation Buttons and Submit Section Button at the bottom */}
              {currentQuestion && !isLoading && (
                <div className="w-full mt-8">
                  <div className="flex justify-between">
                    <motion.button
                      onClick={handlePrevious}
                      disabled={currentQuestionIndex === 0}
                      className={`px-3 sm:px-6 py-2 rounded-lg text-sm sm:text-base transition-colors ${
                        currentQuestionIndex === 0
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
                        }`}
                      whileHover={currentQuestionIndex === 0 ? {} : { scale: 1.05 }}
                      whileTap={currentQuestionIndex === 0 ? {} : { scale: 0.95 }}
                    >
                      Previous
                    </motion.button>
                    <motion.button
                      onClick={handleNext}
                      disabled={currentQuestionIndex === questions.length - 1}
                      className={`px-3 sm:px-6 py-2 rounded-lg text-sm sm:text-base transition-colors ${
                        currentQuestionIndex === questions.length - 1
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      whileHover={currentQuestionIndex === questions.length - 1 ? {} : { scale: 1.05 }}
                      whileTap={currentQuestionIndex === questions.length - 1 ? {} : { scale: 0.95 }}
                    >
                      Next
                    </motion.button>
                  </div>
                  <div className="flex justify-center mt-8">
                    <motion.button
                      onClick={handleSubmitSection}
                      className="px-4 sm:px-8 py-2 sm:py-3 bg-red-600 text-white rounded-full shadow hover:shadow-lg hover:shadow-red-500/50 text-sm sm:text-base md:text-lg font-semibold transition-all"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Submit Section
                    </motion.button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Modals: Start, Break, Submit, End */}
        {/* Start/Break Modal */}
        {showModuleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full mx-4 text-center border border-white/20 dark:border-gray-700/50"
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-zonaProUltra mb-3 sm:mb-4" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>
                {phase === 'break' ? '10 Minute Break' : `${moduleConfig?.title} is going to start`}
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-900 dark:text-gray-100 mb-4 sm:mb-6 md:mb-8">
                {phase === 'break'
                  ? 'Take a break before the Math modules start.'
                  : `There will be ${moduleConfig?.questionCount} questions and time will be ${moduleConfig?.time / 60} minutes.`}
              </p>
              <div className="flex justify-center gap-3 sm:gap-4">
                <motion.button
                  onClick={handleStartModule}
                  className="py-2 px-4 sm:px-6 bg-[#34c759] text-white text-sm sm:text-base md:text-lg rounded-full font-zonaProUltra hover:shadow-lg hover:shadow-[#34c759]/50 transition-all relative"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Start {startCountdown > 0 ? `(${startCountdown}s)` : ''}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {/* Break Timer Modal */}
        {showBreakTimerModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full mx-4 text-center border border-white/20 dark:border-gray-700/50"
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-zonaProUltra mb-3 sm:mb-4" style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>10 Minute Break</h2>
              <p className="text-sm sm:text-base md:text-lg text-black dark:text-gray-100 mb-4 sm:mb-6 md:mb-8">Take a break before the Math modules start.</p>
              <div className="text-2xl sm:text-3xl md:text-4xl font-mono mb-4 sm:mb-6 md:mb-8 text-gray-800 dark:text-white">{formatTime(timer)}</div>
              <motion.button
                onClick={handleBreakEnd}
                className="py-2 px-4 sm:px-8 bg-[#34c759] text-white text-sm sm:text-base md:text-lg rounded-full font-zonaProUltra hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Skip Break
              </motion.button>
            </motion.div>
          </motion.div>
        )}
        {/* Submit Section Modal */}
        {showSubmitModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full mx-4 text-center border border-white/20 dark:border-gray-700/50"
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-zonaProUltra text-red-600 dark:text-red-400 mb-3 sm:mb-4">Submit Section?</h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-900 dark:text-gray-100 mb-4 sm:mb-6 md:mb-8">
                {unansweredCount > 0
                  ? `You have ${unansweredCount} unanswered question${unansweredCount > 1 ? 's' : ''}. Are you sure you want to submit?`
                  : 'All questions are answered. Are you sure you want to submit?'}
              </p>
              <div className="flex justify-center gap-3 sm:gap-4">
                {!submitModalFromTimeout && (
                  <motion.button
                    onClick={handleCancelSubmit}
                    className="py-2 px-4 sm:px-6 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm sm:text-base md:text-lg rounded-full font-zonaProUltra hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    disabled={isSubmitting}
                    whileHover={isSubmitting ? {} : { scale: 1.05 }}
                    whileTap={isSubmitting ? {} : { scale: 0.95 }}
                  >
                    Cancel
                  </motion.button>
                )}
                <motion.button
                  onClick={handleConfirmSubmit}
                  className="py-2 px-4 sm:px-6 bg-red-600 text-white text-sm sm:text-base md:text-lg rounded-full font-zonaProUltra hover:shadow-lg hover:shadow-red-500/50 transition-all"
                  disabled={isSubmitting}
                  whileHover={isSubmitting ? {} : { scale: 1.05 }}
                  whileTap={isSubmitting ? {} : { scale: 0.95 }}
                >
                  {isSubmitting
                    ? 'Submitting...'
                    : `Submit Section${submitCountdown > 0 ? ` (${submitCountdown}s)` : ''}`}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full mx-4 text-center border border-white/20 dark:border-gray-700/50"
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-zonaProUltra text-red-600 dark:text-red-400 mb-3 sm:mb-4">Unable to Display Questions</h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-900 dark:text-gray-100 mb-4 sm:mb-6 md:mb-8">
                We're having trouble loading the questions right now. Please try again later.
              </p>
              <div className="flex flex-col items-center gap-3 sm:gap-4">
                <motion.button
                  onClick={handleErrorModalButtonClick}
                  className="py-2 sm:py-3 px-4 sm:px-8 bg-[#34c759] text-white text-sm sm:text-base md:text-lg rounded-full font-zonaProUltra hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Go Back to Practice Page ({errorModalTimer}s)
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Calculator */}
        <Calculator
          isOpen={isCalculatorOpen}
          onClose={() => setIsCalculatorOpen(false)}
          categoryType={currentQuestion?.category_type || MODULE_CONFIG[phase]?.contentName}
        />
      </div>
    </div>
  );
};

export default SatTestPage;